import { useMemo, useState, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import type { TaskWithDependencies } from '../hooks/useTaskDependencies';
import { laborBucketConfig } from '../lib/laborBucketConfig';
import { formatMinutes } from '../lib/utils';

// ── Layout constants ────────────────────────────────────────────────────────
const NODE_WIDTH = 130;
const WAITING_NODE_WIDTH = 72;
const NODE_HEIGHT = 80;
const HORIZONTAL_GAP = 28;
const VERTICAL_GAP = 20;
const PADDING_X = 20;
const PADDING_Y = 20;

// Labor buckets that represent waiting periods (no person assigned)
const WAITING_BUCKETS = new Set([
  'awaiting_confirmation',
  'awaiting_shipment',
  'awaiting_receipt',
  'waiting_for_parts',
]);

// ── Types ───────────────────────────────────────────────────────────────────

interface LayoutNode {
  task: TaskWithDependencies;
  layer: number;
  columnIndex: number; // visual column position (after merging)
  row: number;
  x: number;
  y: number;
  nodeWidth: number;
}

interface Edge {
  from: LayoutNode;
  to: LayoutNode;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function getNodeAccentColor(laborBucket: string): string {
  const config = laborBucketConfig[laborBucket];
  if (!config) return 'rgb(156, 163, 175)'; // gray-400
  // Map tailwind color classes to actual rgb values for SVG
  const colorMap: Record<string, string> = {
    'bg-blue-500': 'rgb(59, 130, 246)',
    'bg-indigo-500': 'rgb(99, 102, 241)',
    'bg-purple-500': 'rgb(168, 85, 247)',
    'bg-pink-500': 'rgb(236, 72, 153)',
    'bg-violet-500': 'rgb(139, 92, 246)',
    'bg-orange-500': 'rgb(249, 115, 22)',
    'bg-amber-500': 'rgb(245, 158, 11)',
    'bg-yellow-500': 'rgb(234, 179, 8)',
    'bg-green-500': 'rgb(34, 197, 94)',
    'bg-teal-500': 'rgb(20, 184, 166)',
    'bg-cyan-500': 'rgb(6, 182, 212)',
    'bg-gray-400': 'rgb(156, 163, 175)',
    'bg-lime-500': 'rgb(132, 204, 22)',
    'bg-emerald-500': 'rgb(16, 185, 129)',
    'bg-green-700': 'rgb(21, 128, 61)',
    'bg-gray-300': 'rgb(209, 213, 219)',
    'bg-red-300': 'rgb(252, 165, 165)',
    'bg-yellow-400': 'rgb(250, 204, 21)',
    'bg-yellow-300': 'rgb(253, 224, 71)',
    'bg-yellow-200': 'rgb(254, 240, 138)',
  };
  return colorMap[config.color] ?? 'rgb(156, 163, 175)';
}

function getStatusBorderColor(status: string): string {
  const borderMap: Record<string, string> = {
    pending: 'rgb(209, 213, 219)',     // gray-300
    ready: 'rgb(59, 130, 246)',         // blue-500
    staged: 'rgb(168, 85, 247)',        // purple-500
    partial_staged: 'rgb(249, 115, 22)',// orange-500
    in_progress: 'rgb(59, 130, 246)',   // blue-500
    complete: 'rgb(34, 197, 94)',       // green-500
    cancelled: 'rgb(209, 213, 219)',    // gray-300
    abandoned: 'rgb(252, 165, 165)',    // red-300
  };
  return borderMap[status] ?? 'rgb(209, 213, 219)';
}

function getLaborLabel(laborBucket: string): string {
  const config = laborBucketConfig[laborBucket];
  return config?.label ?? laborBucket.replace(/_/g, ' ');
}

// ── Topological Sort & Layer Assignment ─────────────────────────────────────

/**
 * Assigns each task to a "layer" (column in the flow chart).
 * Layer 0 = tasks with no predecessors (roots).
 * Layer N = tasks whose predecessors are all in layers < N.
 *
 * Uses a modified Kahn's algorithm that assigns the longest-path layer
 * to each node so that tasks are pushed as far right as their deps allow.
 */
function assignLayers(tasks: TaskWithDependencies[]): Map<string, number> {
  const taskMap = new Map(tasks.map((t) => [t.id, t]));
  const taskIds = new Set(tasks.map((t) => t.id));

  // Build adjacency: predecessors for each task (filtered to tasks in this set)
  const predecessors = new Map<string, string[]>();
  for (const task of tasks) {
    const preds = task.dependencies
      .map((d) => d.taskId)
      .filter((id) => taskIds.has(id));
    predecessors.set(task.id, preds);
  }

  // Build forward adjacency (successors) for propagation
  const successors = new Map<string, string[]>();
  for (const id of taskIds) {
    successors.set(id, []);
  }
  for (const [taskId, preds] of predecessors) {
    for (const pred of preds) {
      successors.get(pred)?.push(taskId);
    }
  }

  // Longest-path layer assignment using topological order
  const inDegree = new Map<string, number>();
  for (const [taskId, preds] of predecessors) {
    inDegree.set(taskId, preds.length);
  }

  const layers = new Map<string, number>();
  const queue: string[] = [];

  // Start with tasks that have no predecessors
  for (const [taskId, deg] of inDegree) {
    if (deg === 0) {
      queue.push(taskId);
      layers.set(taskId, 0);
    }
  }

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentLayer = layers.get(current) ?? 0;

    for (const succ of successors.get(current) ?? []) {
      // Push successor to at least currentLayer + 1
      const existing = layers.get(succ) ?? 0;
      layers.set(succ, Math.max(existing, currentLayer + 1));

      const newDeg = (inDegree.get(succ) ?? 1) - 1;
      inDegree.set(succ, newDeg);
      if (newDeg === 0) {
        queue.push(succ);
      }
    }
  }

  // Handle any tasks not reached (cycles or orphans) - assign layer 0
  for (const task of tasks) {
    if (!layers.has(task.id)) {
      layers.set(task.id, 0);
    }
  }

  return layers;
}

/**
 * Barycenter ordering: sort nodes within each layer so that nodes connected
 * to upper predecessors are placed higher, minimizing edge crossings.
 *
 * For each node in layer N, compute the average row of its predecessors in
 * layer N-1, then sort by that average.
 */
function barycentricOrder(
  layerGroups: Map<number, TaskWithDependencies[]>,
  sortedLayers: number[],
  taskIds: Set<string>
): void {
  // Build a map of task id -> row index (updated as we reorder)
  const rowOf = new Map<string, number>();

  // Initialize first layer by laborBucket (no predecessors to sort by)
  const firstGroup = layerGroups.get(sortedLayers[0]!) ?? [];
  firstGroup.sort((a, b) => a.laborBucket.localeCompare(b.laborBucket));
  firstGroup.forEach((t, i) => rowOf.set(t.id, i));

  // For each subsequent layer, sort by barycenter of predecessors
  for (let li = 1; li < sortedLayers.length; li++) {
    const group = layerGroups.get(sortedLayers[li]!) ?? [];

    const barycenter = new Map<string, number>();
    for (const task of group) {
      const predRows: number[] = [];
      for (const dep of task.dependencies) {
        if (!taskIds.has(dep.taskId)) continue;
        const r = rowOf.get(dep.taskId);
        if (r !== undefined) predRows.push(r);
      }
      if (predRows.length > 0) {
        barycenter.set(task.id, predRows.reduce((a, b) => a + b, 0) / predRows.length);
      } else {
        // No predecessors in the previous layer: keep relative order
        barycenter.set(task.id, Infinity);
      }
    }

    group.sort((a, b) => {
      const ba = barycenter.get(a.id) ?? Infinity;
      const bb = barycenter.get(b.id) ?? Infinity;
      if (ba !== bb) return ba - bb;
      // Tie-break: alphabetical by laborBucket for determinism
      return a.laborBucket.localeCompare(b.laborBucket);
    });

    group.forEach((t, i) => rowOf.set(t.id, i));
  }
}

/**
 * Compute the full layout: nodes with x, y positions and edges.
 */
function computeLayout(tasks: TaskWithDependencies[]): {
  nodes: LayoutNode[];
  edges: Edge[];
  width: number;
  height: number;
  maxBottom: number;
} {
  if (tasks.length === 0) {
    return { nodes: [], edges: [], width: 0, height: 0, maxBottom: 0 };
  }

  const layers = assignLayers(tasks);
  const taskIds = new Set(tasks.map((t) => t.id));

  // Collapse consecutive all-waiting layers into a single layer.
  // This stacks waiting tasks vertically instead of spreading them across columns.
  const rawLayerGroups = new Map<number, TaskWithDependencies[]>();
  for (const task of tasks) {
    const layer = layers.get(task.id) ?? 0;
    const group = rawLayerGroups.get(layer) ?? [];
    group.push(task);
    rawLayerGroups.set(layer, group);
  }
  const rawSorted = Array.from(rawLayerGroups.keys()).sort((a, b) => a - b);

  // Identify runs of consecutive all-waiting layers and merge them
  let mergeTarget: number | null = null;
  for (const layerIdx of rawSorted) {
    const group = rawLayerGroups.get(layerIdx)!;
    const allWaiting = group.every((t) => WAITING_BUCKETS.has(t.laborBucket));
    if (allWaiting) {
      if (mergeTarget === null) {
        mergeTarget = layerIdx; // first waiting layer in a run
      } else {
        // Merge into the first waiting layer of this run
        for (const t of group) {
          layers.set(t.id, mergeTarget);
        }
      }
    } else {
      mergeTarget = null;
    }
  }

  // Rebuild groups after merging
  const layerGroups = new Map<number, TaskWithDependencies[]>();
  for (const task of tasks) {
    const layer = layers.get(task.id) ?? 0;
    const group = layerGroups.get(layer) ?? [];
    group.push(task);
    layerGroups.set(layer, group);
  }

  // Sort layer keys
  const sortedLayers = Array.from(layerGroups.keys()).sort((a, b) => a - b);

  // Apply barycentric ordering to minimize crossings
  barycentricOrder(layerGroups, sortedLayers, taskIds);

  // Compute per-layer column width (waiting-only layers are narrower)
  const layerWidths = new Map<number, number>();
  for (const [layerIdx, group] of layerGroups) {
    const allWaiting = group.every((t) => WAITING_BUCKETS.has(t.laborBucket));
    layerWidths.set(layerIdx, allWaiting ? WAITING_NODE_WIDTH : NODE_WIDTH);
  }

  // Compute cumulative x offsets per layer
  const layerX = new Map<number, number>();
  let cumulativeX = PADDING_X;
  for (const layerIdx of sortedLayers) {
    layerX.set(layerIdx, cumulativeX);
    cumulativeX += (layerWidths.get(layerIdx) ?? NODE_WIDTH) + HORIZONTAL_GAP;
  }

  // Map layer index → visual column index (for adjacency in edge routing)
  const layerToColumn = new Map<number, number>();
  sortedLayers.forEach((layerIdx, colIdx) => layerToColumn.set(layerIdx, colIdx));

  // Build layout nodes — align each node's Y with its primary predecessor's Y.
  // This prevents lines from crossing behind sibling cards.
  const nodeMap = new Map<string, LayoutNode>();

  // First layer: sequential
  {
    const group = layerGroups.get(sortedLayers[0]!) ?? [];
    const w = layerWidths.get(sortedLayers[0]!) ?? NODE_WIDTH;
    const col = layerToColumn.get(sortedLayers[0]!)!;
    for (let row = 0; row < group.length; row++) {
      const task = group[row]!;
      const x = layerX.get(sortedLayers[0]!)!;
      const y = PADDING_Y + row * (NODE_HEIGHT + VERTICAL_GAP);
      nodeMap.set(task.id, { task, layer: sortedLayers[0]!, columnIndex: col, row, x, y, nodeWidth: w });
    }
  }

  // Subsequent layers: each node targets the median Y of its predecessors
  for (let li = 1; li < sortedLayers.length; li++) {
    const layerIdx = sortedLayers[li]!;
    const group = layerGroups.get(layerIdx) ?? [];
    const x = layerX.get(layerIdx)!;
    const w = layerWidths.get(layerIdx) ?? NODE_WIDTH;
    const col = layerToColumn.get(layerIdx)!;

    const items: Array<{ task: TaskWithDependencies; targetY: number }> = [];
    for (const task of group) {
      const predYs: number[] = [];
      for (const dep of task.dependencies) {
        const pn = nodeMap.get(dep.taskId);
        if (pn) predYs.push(pn.y);
      }
      let targetY: number;
      if (predYs.length === 0) {
        targetY = PADDING_Y;
      } else if (predYs.length === 1) {
        targetY = predYs[0]!;
      } else {
        predYs.sort((a, b) => a - b);
        const mid = Math.floor(predYs.length / 2);
        targetY = predYs.length % 2 === 0
          ? (predYs[mid - 1]! + predYs[mid]!) / 2
          : predYs[mid]!;
      }
      items.push({ task, targetY });
    }

    // For merged waiting columns, sort by dependency chain order (topological)
    // and start from the top of the column to keep compact.
    const allWaitingInLayer = items.every((i) => WAITING_BUCKETS.has(i.task.laborBucket));
    if (allWaitingInLayer && items.length > 1) {
      items.sort((a, b) => {
        const bDepsOnA = b.task.dependencies.some((d) => d.taskId === a.task.id);
        if (bDepsOnA) return -1;
        const aDepsOnB = a.task.dependencies.some((d) => d.taskId === b.task.id);
        if (aDepsOnB) return 1;
        return a.task.id.localeCompare(b.task.id);
      });

      // Stack from top, ignoring predecessor Y targets
      let lastBottom = -Infinity;
      for (let row = 0; row < items.length; row++) {
        const { task } = items[row]!;
        const y = Math.max(lastBottom + VERTICAL_GAP, PADDING_Y);
        lastBottom = y + NODE_HEIGHT;
        nodeMap.set(task.id, { task, layer: layerIdx, columnIndex: col, row, x, y, nodeWidth: w });
      }
    } else {
      items.sort((a, b) => a.targetY - b.targetY);

      let lastBottom = -Infinity;
      for (let row = 0; row < items.length; row++) {
        const { task, targetY } = items[row]!;
        const y = Math.max(targetY, lastBottom + VERTICAL_GAP, PADDING_Y);
        lastBottom = y + NODE_HEIGHT;
        nodeMap.set(task.id, { task, layer: layerIdx, columnIndex: col, row, x, y, nodeWidth: w });
      }
    }
  }

  // Build edges
  const edges: Edge[] = [];
  for (const task of tasks) {
    const toNode = nodeMap.get(task.id);
    if (!toNode) continue;

    for (const dep of task.dependencies) {
      if (!taskIds.has(dep.taskId)) continue;
      const fromNode = nodeMap.get(dep.taskId);
      if (fromNode) {
        edges.push({ from: fromNode, to: toNode });
      }
    }
  }

  const allNodes = Array.from(nodeMap.values());
  const maxBottom = allNodes.length > 0 ? Math.max(...allNodes.map(n => n.y + NODE_HEIGHT)) : 0;

  // Check if any edges span 2+ layers (need below-card routing channel)
  const hasLongEdges = edges.some(e => e.to.columnIndex - e.from.columnIndex > 1);
  const routingChannelHeight = hasLongEdges ? 60 : 0;

  // Width = rightmost node x + its width + padding
  const rightmostNode = allNodes.reduce((a, b) => (a.x + a.nodeWidth > b.x + b.nodeWidth ? a : b), allNodes[0]!);
  const width = rightmostNode.x + rightmostNode.nodeWidth + PADDING_X;
  const height = maxBottom + routingChannelHeight + PADDING_Y;

  return {
    nodes: Array.from(nodeMap.values()),
    edges,
    width,
    height,
    maxBottom,
  };
}

// ── SVG Arrow Path (orthogonal routing) ─────────────────────────────────────

/** Corner radius for orthogonal bends */
const BEND_RADIUS = 8;

/**
 * Build a rounded-corner right-angle connector for a single bend.
 * Moves from (x, yFrom) to (x, yTo) then to (xEnd, yTo) — or similar
 * L-shaped paths — with an arc at the corner.
 */
function arcCorner(
  cx: number,
  cy: number,
  fromDir: 'left' | 'right' | 'up' | 'down',
  toDir: 'left' | 'right' | 'up' | 'down',
  r: number
): string {
  // Determine sweep based on turn direction
  // We draw an arc from the point approaching the corner to the point leaving it
  const dx: Record<string, number> = { left: -1, right: 1, up: 0, down: 0 };
  const dy: Record<string, number> = { left: 0, right: 0, up: -1, down: 1 };

  const startX = cx - dx[fromDir]! * r;
  const startY = cy - dy[fromDir]! * r;
  const endX = cx + dx[toDir]! * r;
  const endY = cy + dy[toDir]! * r;

  // Sweep flag: 1 = clockwise, 0 = counter-clockwise
  // Determine from cross product of direction vectors
  const cross = dx[fromDir]! * dy[toDir]! - dy[fromDir]! * dx[toDir]!;
  const sweep = cross > 0 ? 1 : 0;

  return `L ${startX} ${startY} A ${r} ${r} 0 0 ${sweep} ${endX} ${endY}`;
}

/**
 * Build an orthogonal (right-angle) path from source node right edge to
 * target node left edge, routing through the gap between columns.
 *
 * @param from - Source layout node
 * @param to - Target layout node
 * @param gapOffset - Horizontal pixel offset within the gap (to separate
 *                    multiple edges sharing the same gap column)
 */
function buildArrowPath(from: LayoutNode, to: LayoutNode, gapOffset: number, maxBottom: number): string {
  const x1 = from.x + from.nodeWidth;
  const y1 = from.y + NODE_HEIGHT / 2;
  const x2 = to.x;
  const y2 = to.y + NODE_HEIGHT / 2;

  const gapWidth = x2 - x1; // actual gap between these two nodes
  const columnDiff = to.columnIndex - from.columnIndex;

  // Adjacent columns: route through the gap between them
  if (columnDiff <= 1) {
    // Same row: straight line
    if (Math.abs(y1 - y2) < 1) {
      return `M ${x1} ${y1} L ${x2} ${y2}`;
    }

    const gapCenterX = x1 + gapWidth / 2 + gapOffset;
    const r = Math.min(BEND_RADIUS, Math.abs(y2 - y1) / 2, gapWidth / 4);
    const goingDown = y2 > y1;

    const bend1Dir = goingDown ? 'down' : 'up';
    const arc1 = arcCorner(gapCenterX, y1, 'right', bend1Dir, r);
    const arc2 = arcCorner(gapCenterX, y2, goingDown ? 'down' : 'up', 'right', r);

    return `M ${x1} ${y1} ${arc1} ${arc2} L ${x2} ${y2}`;
  }

  // Long-distance edges (span 2+ columns): route BELOW all cards
  // to avoid crossing behind intermediate nodes.
  const routeY = maxBottom + 20 + Math.abs(gapOffset) * 8;
  const r = BEND_RADIUS;
  const exitX = x1 + HORIZONTAL_GAP / 4;
  const entryX = x2 - HORIZONTAL_GAP / 4;

  // Path: right from source → down to route Y → across below cards → up to target → into target
  return [
    `M ${x1} ${y1}`,
    `L ${exitX - r} ${y1}`,
    `A ${r} ${r} 0 0 1 ${exitX} ${y1 + r}`,
    `L ${exitX} ${routeY - r}`,
    `A ${r} ${r} 0 0 0 ${exitX + r} ${routeY}`,
    `L ${entryX - r} ${routeY}`,
    `A ${r} ${r} 0 0 0 ${entryX} ${routeY - r}`,
    `L ${entryX} ${y2 + r}`,
    `A ${r} ${r} 0 0 1 ${entryX + r} ${y2}`,
    `L ${x2} ${y2}`,
  ].join(' ');
}

/**
 * Pre-compute per-edge gap offsets so that multiple edges routed through
 * the same gap column (between the same pair of layer indices) are spread
 * out horizontally and don't overlap.
 */
function computeGapOffsets(edges: Edge[]): number[] {
  // Group edges by the gap column they route through.
  // The gap column is identified by the source node's layer index.
  const gapGroups = new Map<number, number[]>();
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i]!;
    // Only edges with vertical travel need gap routing
    const y1 = e.from.y + NODE_HEIGHT / 2;
    const y2 = e.to.y + NODE_HEIGHT / 2;
    if (Math.abs(y1 - y2) < 1) continue;

    const gapKey = e.from.columnIndex;
    const group = gapGroups.get(gapKey) ?? [];
    group.push(i);
    gapGroups.set(gapKey, group);
  }

  const offsets = new Array<number>(edges.length).fill(0);
  const EDGE_SPACING = 6; // pixels between parallel edges in the same gap

  for (const indices of gapGroups.values()) {
    if (indices.length <= 1) continue;
    // Sort edges within the group by their vertical midpoint for consistent ordering
    indices.sort((a, b) => {
      const midA = (edges[a]!.from.y + edges[a]!.to.y) / 2;
      const midB = (edges[b]!.from.y + edges[b]!.to.y) / 2;
      return midA - midB;
    });
    // Center the group of offsets around 0
    const totalWidth = (indices.length - 1) * EDGE_SPACING;
    for (let j = 0; j < indices.length; j++) {
      offsets[indices[j]!] = -totalWidth / 2 + j * EDGE_SPACING;
    }
  }

  return offsets;
}

// ── Component ───────────────────────────────────────────────────────────────

interface TaskFlowChartProps {
  tasks: TaskWithDependencies[];
}

export function TaskFlowChart({ tasks }: TaskFlowChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null);

  const { nodes, edges, width, height, maxBottom, gapOffsets } = useMemo(() => {
    const layout = computeLayout(tasks);
    return { ...layout, gapOffsets: computeGapOffsets(layout.edges) };
  }, [tasks]);

  if (tasks.length === 0) {
    return null;
  }

  // Build a set of unique labor buckets for the legend
  const laborBuckets = Array.from(new Set(tasks.map((t) => t.laborBucket)));

  return (
    <div className="mb-4 min-w-0 overflow-hidden">
      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-3 text-xs">
        {laborBuckets.map((bucket) => (
          <div key={bucket} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded"
              style={{ backgroundColor: getNodeAccentColor(bucket) }}
            />
            <span className="text-foreground-muted">{getLaborLabel(bucket)}</span>
          </div>
        ))}
      </div>

      {/* Flow chart container */}
      <div
        ref={containerRef}
        className="overflow-x-auto border border-border rounded-lg bg-background-subtle"
      >
        <svg
          width={Math.max(width, 300)}
          height={Math.max(height, 120)}
          viewBox={`0 0 ${Math.max(width, 300)} ${Math.max(height, 120)}`}
          className="block"
        >
          {/* Arrow marker definition */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="8"
              markerHeight="6"
              refX="8"
              refY="3"
              orient="auto"
              markerUnits="userSpaceOnUse"
            >
              <polygon
                points="0 0, 8 3, 0 6"
                fill="rgb(156, 163, 175)"
              />
            </marker>
            <marker
              id="arrowhead-highlighted"
              markerWidth="8"
              markerHeight="6"
              refX="8"
              refY="3"
              orient="auto"
              markerUnits="userSpaceOnUse"
            >
              <polygon
                points="0 0, 8 3, 0 6"
                fill="rgb(59, 130, 246)"
              />
            </marker>
          </defs>

          {/* Edges (arrows) */}
          {edges.map((edge, i) => {
            const isHighlighted =
              hoveredTaskId === edge.from.task.id ||
              hoveredTaskId === edge.to.task.id;

            return (
              <path
                key={`edge-${i}`}
                d={buildArrowPath(edge.from, edge.to, gapOffsets[i] ?? 0, maxBottom)}
                fill="none"
                stroke={isHighlighted ? 'rgb(59, 130, 246)' : 'rgb(209, 213, 219)'}
                strokeWidth={isHighlighted ? 2 : 1.5}
                markerEnd={
                  isHighlighted
                    ? 'url(#arrowhead-highlighted)'
                    : 'url(#arrowhead)'
                }
                className="transition-colors duration-150"
              />
            );
          })}

          {/* Nodes */}
          {nodes.map((node) => {
            const isHovered = hoveredTaskId === node.task.id;
            const borderColor = getStatusBorderColor(node.task.status);
            const accentColor = getNodeAccentColor(node.task.laborBucket);
            const isWaiting = WAITING_BUCKETS.has(node.task.laborBucket);
            const duration = formatMinutes(
              Math.round(
                (node.task.estimatedMinutesMin + node.task.estimatedMinutesMax) / 2
              )
            );

            return (
              <g
                key={node.task.id}
                onMouseEnter={() => setHoveredTaskId(node.task.id)}
                onMouseLeave={() => setHoveredTaskId(null)}
                className="cursor-default"
              >
                {/* Node background */}
                <rect
                  x={node.x}
                  y={node.y}
                  width={node.nodeWidth}
                  height={NODE_HEIGHT}
                  rx={8}
                  ry={8}
                  fill={isWaiting ? 'rgb(254, 252, 232)' : 'white'}
                  stroke={isHovered ? 'rgb(59, 130, 246)' : borderColor}
                  strokeWidth={isHovered ? 2 : 1.5}
                  strokeDasharray={isWaiting ? '6 3' : undefined}
                  className="transition-all duration-150"
                />

                {/* Left accent bar */}
                <rect
                  x={node.x}
                  y={node.y}
                  width={4}
                  height={NODE_HEIGHT}
                  rx={8}
                  ry={0}
                  fill={accentColor}
                  clipPath={`inset(0 0 0 0 round 8px 0 0 8px)`}
                />
                {/* Accent bar clip: just draw a thin rect over the left edge */}
                <rect
                  x={node.x}
                  y={node.y + 1}
                  width={4}
                  height={NODE_HEIGHT - 2}
                  fill={accentColor}
                />

                {/* Labor bucket label */}
                <text
                  x={node.x + 12}
                  y={node.y + 18}
                  fontSize={11}
                  fontWeight={600}
                  fill="rgb(31, 41, 55)"
                  className="select-none"
                >
                  {truncateText(getLaborLabel(node.task.laborBucket), isWaiting ? 8 : 16)}
                </text>

                {/* Assigned person + duration (or "Waiting" for wait tasks) */}
                <text
                  x={node.x + 12}
                  y={node.y + 34}
                  fontSize={10}
                  fill="rgb(107, 114, 128)"
                  className="select-none"
                >
                  {isWaiting
                    ? duration
                    : <>
                        {node.task.assignedPerson
                          ? truncateText(node.task.assignedPerson.name, 13)
                          : 'Unassigned'}
                        {' \u00B7 '}
                        {duration}
                      </>}
                </text>

                {/* Status badge */}
                <rect
                  x={node.x + 10}
                  y={node.y + NODE_HEIGHT - 26}
                  width={getStatusTextWidth(node.task.status) + 12}
                  height={16}
                  rx={4}
                  fill={getStatusBadgeFill(node.task.status)}
                />
                <text
                  x={node.x + 16}
                  y={node.y + NODE_HEIGHT - 14}
                  fontSize={9}
                  fontWeight={500}
                  fill={getStatusBadgeTextColor(node.task.status)}
                  className="select-none"
                >
                  {formatStatus(node.task.status)}
                </text>

                {/* Scheduled date — show as third line above status badge */}
                {node.task.scheduledStart && (
                  <text
                    x={node.x + 12}
                    y={node.y + 48}
                    fontSize={9}
                    fill="rgb(156, 163, 175)"
                    className="select-none"
                  >
                    {formatShortDate(node.task.scheduledStart)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

// ── Text helpers ────────────────────────────────────────────────────────────

function truncateText(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen - 1) + '\u2026';
}

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ');
}

function formatShortDate(isoString: string): string {
  try {
    return format(parseISO(isoString), 'MMM d');
  } catch (err) {
    console.warn('[TaskFlowChart] Failed to parse date:', isoString, err);
    return '??';
  }
}

function getStatusTextWidth(status: string): number {
  // Approximate width based on character count at 9px font
  const text = formatStatus(status);
  return text.length * 5;
}

function getStatusBadgeFill(status: string): string {
  const map: Record<string, string> = {
    pending: 'rgb(229, 231, 235)',       // gray-200
    ready: 'rgb(219, 234, 254)',          // blue-100
    staged: 'rgb(243, 232, 255)',         // purple-100
    partial_staged: 'rgb(255, 237, 213)', // orange-100
    in_progress: 'rgb(219, 234, 254)',    // blue-100
    complete: 'rgb(220, 252, 231)',       // green-100
    cancelled: 'rgb(243, 244, 246)',      // gray-100
    abandoned: 'rgb(254, 226, 226)',      // red-100
  };
  return map[status] ?? 'rgb(229, 231, 235)';
}

function getStatusBadgeTextColor(status: string): string {
  const map: Record<string, string> = {
    pending: 'rgb(55, 65, 81)',          // gray-700
    ready: 'rgb(29, 78, 216)',            // blue-700
    staged: 'rgb(126, 34, 206)',          // purple-700
    partial_staged: 'rgb(194, 65, 12)',   // orange-700
    in_progress: 'rgb(29, 78, 216)',      // blue-700
    complete: 'rgb(21, 128, 61)',         // green-700
    cancelled: 'rgb(107, 114, 128)',      // gray-500
    abandoned: 'rgb(239, 68, 68)',        // red-500
  };
  return map[status] ?? 'rgb(55, 65, 81)';
}
